import { defineStore } from 'pinia'
import type { BubbleTypes, Bubble, WebPageBubble } from './interfaces'
import { useUserStore } from './user'
import { useRuntimeConfig, navigateTo } from '#app'
import { logError, useLogger } from '@/composables/useLogger'

export const useBubblesStore = defineStore('bubblesStore', {
  state: () => ({
    bubbles: {} as Record<string, Bubble | WebPageBubble>,
    bubblesByType: {} as Record<string, Bubble[]>,
    userBubbleTypes: [] as string[],
    rootBubbles: [] as Bubble[],
    liteBubbles: [] as Bubble[],
    bubbleTypes: {} as BubbleTypes,
    loading: false,
    error: null as string | null,
  }),
  getters: {
    urlBubbleTypes: (state) => {
      return Object.entries(state.bubbleTypes)
        .filter(([_, type]) => type.supports.includes('url'))
        .map(([key, _]) => key)
    },
    textBubbleTypes: (state) => {
      return Object.entries(state.bubbleTypes)
        .filter(([_, type]) => type.supports.includes('text'))
        .map(([key, _]) => key)
    },
    imageBubbleTypes: (state) => {
      return Object.entries(state.bubbleTypes)
        .filter(([_, type]) => type.supports.includes('image'))
        .map(([key, _]) => key)
    },
    YouTubeBubbleTypes: (state) => {
      return Object.entries(state.bubbleTypes)
        .filter(([_, type]) => type.supports.includes('video'))
        .map(([key, _]) => key)
    },
    fileBubbleTypes: (state) => {
      return Object.entries(state.bubbleTypes)
        .filter(([_, type]) => type.supports.includes('file'))
        .map(([key, _]) => key)
    },
    getTypeName: (state) => {
      return (type: string) => {
        const typeInfo = state.bubbleTypes[type]
        return typeInfo ? typeInfo.name : 'Unknown Type'
      }
    },
    getTypeIcon: (state) => {
      return (type: string) => {
        const typeInfo = state.bubbleTypes[type]
        return typeInfo ? typeInfo.icon : 'mdi:help-circle'
      }
    },
    getTypeColor: (state) => {
      return (type: string) => {
        const typeInfo = state.bubbleTypes[type]
        return typeInfo ? typeInfo.color : '#999999'
      }
    },
    isBeta: (state) => {
      return (type: string) => {
        const typeInfo = state.bubbleTypes[type]
        return typeInfo ? typeInfo.beta === true : false
      }
    },
    getBubbleTitle: (state) => {
      return (uuid: string) => {
        const bubble = state.liteBubbles.find((b) => b.uuid === uuid)
        return bubble ? bubble.title || bubble.url || 'Untitled' : 'Untitled'
      }
    },
    getBubbleType: (state) => {
      return (uuid: string) => {
        const bubble = state.liteBubbles.find((b) => b.uuid === uuid)
        return bubble ? bubble.type || 'No type' : 'No type'
      }
    },
  },
  actions: {
    async fetchBubbleTypes() {
      // If already loaded, don't fetch again
      if (Object.keys(this.bubbleTypes).length > 0) return this.bubbleTypes

      // If already loading, don't start another request
      if (this.loading) return

      const runtimeConfig = useRuntimeConfig()
      this.loading = true
      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/bubbles/types`)
        const data = await response.json()
        this.bubbleTypes = data as BubbleTypes
        return this.bubbleTypes
      } catch (error) {
        logError('Error fetching bubble types:', error)
        this.error = 'Error fetching bubble types. Please try again.'
        return null
      } finally {
        this.loading = false
      }
    },
    async fetchBubbles(skip: number = 0, limit: number = 100) {
      const runtimeConfig = useRuntimeConfig()
      this.loading = true
      try {
        const response = await fetch(
          `${runtimeConfig.public.apiBase}/bubbles/list?skip=${skip}&limit=${limit}`
        )
        const data = await response.json()
        this.bubbles = (data as Bubble[]).reduce(
          (acc, bubble) => {
            acc[bubble.uuid] = bubble
            return acc
          },
          {} as Record<string, Bubble>
        )
        this.filterBubblesByType()
      } catch (error) {
        logError('Error fetching bubbles:', error)
        this.error = 'Error fetching bubbles. Please try again.'
      } finally {
        this.loading = false
      }
    },
    filterBubblesByType() {
      this.bubblesByType = {}
      for (const bubble of Object.values(this.bubbles)) {
        if (!this.bubblesByType[bubble.type]) {
          this.bubblesByType[bubble.type] = []
        }
        this.bubblesByType[bubble.type]?.push(bubble)
      }
    },
    async fetchBubble(bubbleId: string) {
      try {
        const response = await fetch(`${useRuntimeConfig().public.apiBase}/bubble/${bubbleId}`)

        if (response.status === 401) {
          // Handle unauthorized error
          const userStore = useUserStore()
          await userStore.logout()
          navigateTo('/auth')
          return null
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        // Update the bubble while preserving existing bubbles
        this.bubbles = {
          ...this.bubbles,
          [bubbleId]: data as Bubble | WebPageBubble,
        }

        // If this is a new bubble type, fetch all bubbles of this type
        const bubbleType = (data as Bubble).type
        if (!this.bubblesByType[bubbleType] || this.bubblesByType[bubbleType].length <= 1) {
          await this.fetchBubblesByType(bubbleType)
        }

        return this.bubbles[bubbleId]
      } catch (error) {
        logError('Error fetching bubble:', error)
        throw error
      }
    },
    async fetchBubblesByType(type: string, skip: number = 0, limit: number = 1000) {
      const runtimeConfig = useRuntimeConfig()
      this.loading = true
      try {
        const response = await fetch(
          `${runtimeConfig.public.apiBase}/bubbles/list/${type}?skip=${skip}&limit=${limit}`
        )
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        this.bubblesByType[type] = data as Bubble[]
      } catch (error) {
        logError(`Error fetching bubbles of type ${type}:`, error)
        this.error = `Error fetching bubbles of type ${type}. Please try again.`
      } finally {
        this.loading = false
      }
    },
    async fetchLiteBubbles(skip: number = 0, limit: number = 5000) {
      const runtimeConfig = useRuntimeConfig()
      this.loading = true
      try {
        const response = await fetch(
          `${runtimeConfig.public.apiBase}/bubbles/list/lite?skip=${skip}&limit=${limit}`
        )
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        this.liteBubbles = data as Bubble[]
      } catch (error) {
        logError('Error fetching root bubbles:', error)
        this.error = 'Error fetching root bubbles. Please try again.'
      } finally {
        this.loading = false
      }
    },
    async fetchBubbleTypeDetails(type: string) {
      const runtimeConfig = useRuntimeConfig()
      this.loading = true
      try {
        const response = await fetch(`${runtimeConfig.public.apiBase}/bubbles/types/${type}`)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        return data
      } catch (error) {
        logError(`Error fetching details for bubble type ${type}:`, error)
        this.error = `Error fetching details for bubble type ${type}. Please try again.`
      } finally {
        this.loading = false
      }
    },
    getBubbleTypeInfo(type: string) {
      const defaultType = {
        name: 'Unknown Type',
        icon: 'mdi:help-circle',
        color: '#999999',
      }
      return type === 'unknown' ? defaultType : this.bubbleTypes[type] || defaultType
    },
    async fetchUserBubbleTypes(): Promise<string[]> {
      const logger = useLogger()

      // Log current state
      logger.log('Fetching user bubble types. Current state:', {
        currentTypes: this.userBubbleTypes,
        isLoading: this.loading,
      })

      // Create a promise that resolves when the current request is done
      if (this.loading) {
        logger.log('Already loading user bubble types, waiting for current request...')
        // Wait a bit and try again
        await new Promise((resolve) => setTimeout(resolve, 100))
        return this.fetchUserBubbleTypes()
      }

      const runtimeConfig = useRuntimeConfig()
      this.loading = true

      try {
        logger.log('Making request to fetch user bubble types')
        const response = await fetch(`${runtimeConfig.public.apiBase}/bubbles/types/user`)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        logger.log('Received user bubble types:', data)

        // Ensure we got an array
        if (!Array.isArray(data)) {
          throw new Error('Expected array of user bubble types but got: ' + typeof data)
        }

        this.userBubbleTypes = data
        return this.userBubbleTypes
      } catch (error) {
        logError('Error fetching user bubble types:', error)
        this.error = 'Error fetching user bubble types. Please try again.'
        // Don't return null, keep existing data if any
        return this.userBubbleTypes
      } finally {
        this.loading = false
      }
    },
    addBubble(bubble: Bubble) {
      this.bubbles = {
        ...this.bubbles,
        [bubble.uuid]: bubble,
      }

      // Also update bubblesByType
      if (!this.bubblesByType[bubble.type]) {
        this.bubblesByType[bubble.type] = []
      }

      const bubbleArray = this.bubblesByType[bubble.type]
      if (!bubbleArray) {
        this.bubblesByType[bubble.type] = [bubble]
        return
      }

      // Check if bubble already exists in bubblesByType
      const existingIndex = bubbleArray.findIndex((b) => b.uuid === bubble.uuid)
      if (existingIndex === -1) {
        bubbleArray.push(bubble)
      } else {
        bubbleArray[existingIndex] = bubble
      }
    },
    async addBubbleFromShare(shareUuid: string, bubbleUuid?: string): Promise<Bubble | null> {
      const logger = useLogger()
      const runtimeConfig = useRuntimeConfig()
      this.loading = true
      this.error = null

      try {
        let response: Bubble
        if (bubbleUuid) {
          // Ingest a single bubble from a shared collection
          logger.log(`Ingesting single bubble ${bubbleUuid} from collection share ${shareUuid}`)
          response = await $fetch<Bubble>(
            `${runtimeConfig.public.apiBase}/collections/ingest/${shareUuid}/bubbles/${bubbleUuid}`,
            {
              method: 'POST',
            }
          )
        } else {
          // Ingest a direct single bubble share (legacy)
          logger.log(`Ingesting direct bubble share ${shareUuid}`)
          response = await $fetch<Bubble>(
            `${runtimeConfig.public.apiBase}/bubbles/add-from-share`,
            {
              method: 'POST',
              body: { share_uuid: shareUuid },
            }
          )
        }

        // Add the new bubble to the store
        this.addBubble(response)
        logger.log('Successfully added bubble from share:', response)

        return response
      } catch (error) {
        logError('Error adding bubble from share:', error)
        this.error = 'Could not add the bubble to your collection. Please try again.'
        return null
      } finally {
        this.loading = false
      }
    },
  },
})
